// eslint-disable-next-line import-x/no-restricted-paths
import type { AiAgentTone } from '@src/app/Automations/PersonalityV1/agentPersonalityConstants'

// eslint-disable-next-line import-x/no-restricted-paths
import { OdysseyAnalyticsBridge } from '@odyssey/analytics/OdysseyAnalyticsBridge'

export type AiAgentModalTriggerPages = 'automations' | 'workflow builder' | 'billing page'

type ReplySchedule = 'always' | 'duringHours' | 'afterHours' | 'never'

export default class AiAutomationsAnalyticsStore {
  private readonly analytics: typeof OdysseyAnalyticsBridge

  constructor() {
    this.analytics = OdysseyAnalyticsBridge
  }

  /**
   * Tracks when the automations page is navigated.
   */
  automationsPageNavigated(page: 'sona') {
    void this.analytics.track('automations_page_navigated', { page })
  }

  /**
   * Tracks when the get ai agent button is clicked on the automations page
   * or from the workflow builder
   */
  getAiAgentClicked(page: AiAgentModalTriggerPages) {
    void this.analytics.track('automations_get_ai_agent_clicked', { page })
  }

  /**
   * Tracks when the AI agent trial modal was seen
   */
  aiAgentTrialModalViewed(page: AiAgentModalTriggerPages) {
    void this.analytics.track('ai_agent_trial_modal_viewed', { page })
  }

  /**
   * Tracks when the AI agent trial summary modal was seen
   */
  aiAgentTrialSummaryViewed(page: AiAgentModalTriggerPages) {
    void this.analytics.track('ai_agent_trial_billing_summary_viewed', { page })
  }

  /**
   * Tracks when the AI agent trial inbox selection modal was seen
   */
  aiAgentTrialInboxesViewed() {
    void this.analytics.track('ai_agent_trial_choose_number_viewed')
  }

  /**
   * Tracks when the confirm and pay for ai agent button is clicked on the automations page
   * or from the workflow builder
   */
  confirmAndPayForAiAgentClicked(page: AiAgentModalTriggerPages) {
    void this.analytics.track('automations_confirm_and_pay_for_ai_agent_clicked', {
      page,
    })
  }

  /**
   * Tracks when the ai agent greeting is updated
   */
  aiAgentGreetingUpdated(newGreeting: string) {
    void this.analytics.track('automations_ai_agent_greeting_updated', { newGreeting })
  }

  /**
   * Tracks when the view_calls button is clicked on the ai agent settings page
   */
  viewCallsWithAiAutomationsOnCallsView(phoneNumber: string) {
    void this.analytics.track('view_calls_with_ai_automations_calls_view', {
      phoneNumber,
    })
  }

  /**
   * Tracks when the add knowledge button is clicked
   */
  addKnowledgeClicked() {
    void this.analytics.track('automations_add_knowledge_clicked')
  }

  /**
   * Tracks when a knowledge page is uploaded
   */
  uploadKnowledgePage(files: File[]) {
    const fileNames = files.map((file) => file.name).join(', ')
    void this.analytics.track('automations_knowledge_page_uploaded', { fileNames })
  }

  /**
   * Tracks when a website import is initiated to create a knowledge page
   */
  websiteImported(url: string) {
    void this.analytics.track('automations_knowledge_page_website_imported', { url })
  }

  /**
   * Tracks when a knowledge page is created
   */
  createKnowledgePage({ name, content }: { name: string; content: string }) {
    void this.analytics.track('automations_knowledge_page_created', {
      pageName: name,
      pageContent: content,
    })
  }

  /**
   * Tracks when a knowledge page is updated
   */
  updateKnowledgePage({
    previousPageName,
    previousPageContent,
    newPageName,
    newPageContent,
  }: {
    previousPageName: string
    previousPageContent: string
    newPageName: string
    newPageContent: string
  }) {
    void this.analytics.track('automations_knowledge_page_updated', {
      previousPageName,
      previousPageContent,
      newPageName,
      newPageContent,
    })
  }

  /**
   * Tracks when a failed knowledge page is retried
   */
  retryFailedKnowledgePage(pageTitle: string) {
    void this.analytics.track('automations_knowledge_page_retry_failed_page', {
      pageTitle,
    })
  }

  /**
   * Tracks when a knowledge page is deleted from the workspace
   */
  deleteKnowledgePageClicked(pageTitle: string) {
    void this.analytics.track('automations_knowledge_page_deleted', { pageTitle })
  }

  /**
   * Tracks when a knowledge page is removed from the ai agent
   */
  removeKnowledgePageAccess(pageTitle: string) {
    void this.analytics.track('automations_knowledge_page_access_removed', { pageTitle })
  }

  /**
   * Tracks when the test ai agent button is clicked
   */
  testAiAgentClicked(location: 'ai_agent_step') {
    void this.analytics.track('automations_test_ai_agent_clicked', { location })
  }

  /**
   * Tracks when the add custom job button is clicked
   */
  addCustomJobClicked() {
    void this.analytics.track('automations_add_custom_job_clicked')
  }

  /**
   * Tracks when the create new job button is clicked
   */
  createNewJobClicked() {
    void this.analytics.track('automations_create_new_job_clicked')
  }

  /**
   * Tracks when the create job from scratch button is clicked
   */
  createJobFromScratchClicked() {
    void this.analytics.track('automations_create_job_from_scratch_clicked')
  }

  /**
   * Tracks when the use template button is clicked
   */
  templateUsedForJobCreationClicked(templateName: string) {
    void this.analytics.track('automations_use_template_clicked', { templateName })
  }

  /**
   * Tracks when a custom job is created
   */
  createCustomJob({
    name,
    description,
    instructions,
  }: {
    name: string
    description: string
    instructions: string
  }) {
    void this.analytics.track('automations_custom_job_created', {
      jobName: name,
      jobDescription: description,
      jobInstructions: instructions,
    })
  }

  /**
   * Tracks when a custom job is updated
   */
  updateCustomJob({
    previousJobName,
    previousJobDescription,
    previousJobInstructions,
    newJobName,
    newJobDescription,
    newJobInstructions,
  }: {
    previousJobName: string
    previousJobDescription: string
    previousJobInstructions: string
    newJobName: string
    newJobDescription: string
    newJobInstructions: string
  }) {
    void this.analytics.track('automations_custom_job_updated', {
      previousJobName,
      previousJobDescription,
      previousJobInstructions,
      newJobName,
      newJobDescription,
      newJobInstructions,
    })
  }

  /**
   * Tracks when a custom job is deleted from the workspace
   */
  deleteCustomJobClicked(jobName: string) {
    void this.analytics.track('automations_custom_job_deleted', { jobName })
  }

  /**
   * Tracks when a custom job is removed from the ai agent
   */
  removeCustomJobAccess(jobName: string) {
    void this.analytics.track('automations_custom_job_access_removed', { jobName })
  }

  /**
   * Tracks when a custom job is enabled for the AI agent (toggle = true for a step)
   */
  addCustomJobAccess(jobName: string) {
    void this.analytics.track('automations_custom_job_access_added', { jobName })
  }

  /**
   * Tracks when the optimize custom job instructions button is clicked
   */
  optimizeCustomJobInstructionsClicked() {
    void this.analytics.track('automations_optimize_custom_job_instructions_clicked')
  }

  /**
   * Tracks when the optimize custom job instructions operation fails
   */
  optimizeCustomJobInstructionsFailed(instructionsLength: number) {
    void this.analytics.track('automations_optimize_custom_job_instructions_failed', {
      instructionsLength,
    })
  }

  /**
   * Tracks when the undo optimize custom job instructions button is clicked
   */
  undoOptimizeCustomJobInstructionsClicked() {
    void this.analytics.track('automations_undo_optimize_custom_job_instructions_clicked')
  }

  /**
   * Tracks when the AI agent vocabulary is updated
   */
  updateAiAgentVocabulary(keyterms: string[]) {
    void this.analytics.track('automations_ai_agent_vocabulary_updated', { keyterms })
  }
  /**
   * Tracks when the AI agent personality tone is changed
   */
  changeAiAgentPersonalityTone({
    user_id,
    previous_tone,
    new_tone,
    timestamp,
  }: {
    user_id: string
    previous_tone: AiAgentTone
    new_tone: AiAgentTone
    timestamp: string
  }) {
    void this.analytics.track('tone_selection_changed', {
      user_id,
      previous_tone,
      new_tone,
      timestamp,
    })
  }

  /**
   * Tracks when the conversation history toggle is changed
   */
  conversationHistoryToggled(enabled: boolean) {
    void this.analytics.track('automations_conversation_history_toggled', { enabled })
  }

  /**
   * Tracks when a user selects a voice in the Sona step of the Call Flow Builder
   */
  sonaVoiceSelected(voiceName: string) {
    void this.analytics.track('automations_ai_agent_voice_selected', {
      voice_name: voiceName,
    })
  }

  /**
   * Tracks when a user selects a language in the Sona step of the Call Flow Builder
   */
  sonaLanguageSelected(selectedLanguage: string) {
    void this.analytics.track('sona_language_selected', {
      selected_language: selectedLanguage,
    })
  }

  /**
   * Tracks when the user views the call flow placement page (Step 1) in the Sona Tab Onboarding Wizard
   */
  sonaTabOnboardingWizardCallFlowPlacementStepViewed() {
    void this.analytics.track(
      'sona_tab_onboarding_wizard_call_flow_placement_step_viewed',
      {},
    )
  }

  /**
   * Tracks when the user views the personalization page with greeting, take a message, and personality settings (Step 2) in the Sona Tab Onboarding Wizard
   */
  sonaTabOnboardingWizardPersonalizationStepViewed() {
    void this.analytics.track(
      'sona_tab_onboarding_wizard_personalization_step_viewed',
      {},
    )
  }

  /**
   * Tracks when the user views the knowledge page (Step 3) in the Sona Tab Onboarding Wizard
   */
  sonaTabOnboardingWizardKnowledgeStepViewed() {
    void this.analytics.track('sona_tab_onboarding_wizard_knowledge_step_viewed', {})
  }

  /**
   * Tracks when the user clicks the "Test Sona" button in the Sona Tab Onboarding Wizard
   */
  sonaTabOnboardingWizardTestSonaClicked() {
    void this.analytics.track('sona_tab_onboarding_wizard_test_sona_clicked', {})
  }

  /**
   * Tracks when the user activates Sona by completing the Sona Tab Onboarding Wizard flow
   */
  sonaTabOnboardingWizardSonaActivated() {
    void this.analytics.track('sona_tab_onboarding_wizard_sona_activated', {})
  }

  /**
   * Tracks when the user imports a website link so Sona can reference it in SMS conversations
   */
  sonaForSmsMemoryWebsiteImported(url: string) {
    void this.analytics.track('sona_for_sms_memory_website_imported', { url })
  }

  /**
   * Tracks when the user opens the detail/settings page for a specific skill
   */
  sonaForSmsSkillSettingsPageViewed(skillName: string) {
    void this.analytics.track('sona_for_sms_skill_settings_page_viewed', { skillName })
  }

  /**
   * Tracks when the user clicks the "Test skill" button on a skill settings page
   */
  sonaForSmsTestSkillClicked(skillName: string) {
    void this.analytics.track('sona_for_sms_test_skill_clicked', { skillName })
  }

  /**
   * Tracks when the user clicks "Activate skill" to enable a skill. `replySchedule`
   * is included when the activation surface lets the user pick a schedule
   * (Always/During hours/After hours) at the same time.
   */
  sonaForSmsSkillActivated(skillName: string, replySchedule?: ReplySchedule) {
    void this.analytics.track('sona_for_sms_skill_activated', {
      skillName,
      replySchedule,
    })
  }

  /**
   * Tracks when the user deactivates/disables a previously activated skill. `replySchedule`
   * is included when deactivation happened via picking "Never" from a reply-schedule control.
   */
  sonaForSmsSkillDeactivated(skillName: string, replySchedule?: ReplySchedule) {
    void this.analytics.track('sona_for_sms_skill_deactivated', {
      skillName,
      replySchedule,
    })
  }

  /**
   * Tracks when the user changes the reply schedule for an already-active
   * skill from the Manage modal (i.e. no activation/deactivation occurs)
   */
  sonaForSmsReplyScheduleUpdated(skillName: string, replySchedule: ReplySchedule) {
    void this.analytics.track('sona_for_sms_reply_schedule_updated', {
      skillName,
      replySchedule,
    })
  }

  /**
   * Tracks when the user takes back control of a conversation that Sona is currently handling
   */
  sonaForSmsConversationControlTakenBack() {
    void this.analytics.track('sona_for_sms_conversation_control_taken_back')
  }
}
